import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  ButtonWrap,
  Icon,
  IconName,
  MobileHotelMapTooltip,
  PremierCollectionBenefitsModal,
} from "halifax";
import React, { useContext } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  IIdLodgings,
  Lodging,
  LodgingCollectionEnum,
  LodgingSelectionEnum,
  SELECTED_HOTEL_FROM_MAP,
  buildEncodedPersistentLodgingTrackingProperties,
  getLodgingTrackingProperties,
} from "redmond";
import {
  HotelDetailsEntrySourceEnum,
  StayType,
} from "redmond/hotels-module/interfaces";
import { localCache, useShowPolicyBanner } from "@capone/common";
import { v4 as uuidv4 } from "uuid";
import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  CACHE_HOTEL_TOKEN,
  LC_GATING_EXPERIMENT,
  LODGING_PROMOTIONS,
  LODGING_PROMOTIONS_AVAILABLE,
  LODGING_PROMOTIONS_VARIANTS,
  LUXURY_PAY_AT_HOTEL_BADGE,
  ALLOWED_PAY_LATER_LODGINGS,
  AllowedPayLaterLodgingsConfig,
  getExperimentVariantJSON,
  PREMIER_COLLECTION_EXPERIMENT,
  STAYS_SEARCH,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  useExperiments,
} from "../../../../context/experiments";
import { isAllowedPayLaterLodging } from "../../../../utils/payLater";
import {
  PATH_SHOP,
  PATH_VACATION_RENTAL_SHOP,
  PREMIER_COLLECTION_PATH_SHOP,
} from "../../../../utils/paths";
import { transformToStringifiedQuery } from "../../../shop/utils/queryStringHelpers";
import { MobileAvailabilityMapRollingGalleryConnectorProps } from "./container";
import "./styles.scss";
import * as textConstants from "./textConstants";
import { ClientContext } from "../../../../App";

export interface IMobileAvailabilityMapRollingGalleryProps
  extends MobileAvailabilityMapRollingGalleryConnectorProps,
    RouteComponentProps {}

export const MobileAvailabilityMapRollingGallery = (
  props: IMobileAvailabilityMapRollingGalleryProps
) => {
  const {
    lodgings,
    lodgingIdInFocus,
    nightCount,
    selectedAccountId,
    hotelQueryParams,
    searchLocation,
    roomsCount,
    petsCount,
    mapBounds,
    isVentureX,
    setLodgingIdInFocus,
    history,
    viewedHotelListProperties,
    isUserPrimary = false,
    largestValueAccount,
  } = props;

  const [openBenefitsModal, setOpenBenefitsModal] = React.useState(false);
  const lodgingInFocus = React.useMemo(
    () =>
      lodgings?.find(
        (lodging: Lodging) => lodging.lodging.id === lodgingIdInFocus
      ),
    [lodgingIdInFocus, lodgings]
  );

  const expState = useExperiments();

  const lodgingPromotions = getExperimentVariantCustomVariants(
    expState.experiments,
    LODGING_PROMOTIONS,
    LODGING_PROMOTIONS_VARIANTS
  );
  const isLodgingPromotionsExperiment = React.useMemo(
    () => lodgingPromotions === LODGING_PROMOTIONS_AVAILABLE,
    [lodgingPromotions]
  );

  const isPayAtHotelBadgeEnabled =
    getExperimentVariant(expState.experiments, LUXURY_PAY_AT_HOTEL_BADGE) ===
    AVAILABLE;

  const allowedPayLaterLodgingIds = React.useMemo(() => {
    const config = getExperimentVariantJSON(
      expState.experiments,
      ALLOWED_PAY_LATER_LODGINGS
    ) as AllowedPayLaterLodgingsConfig | null;
    return config?.data?.lodgingIds;
  }, [expState.experiments]);

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT
    ) === AVAILABLE;

  const isLifestyleCollectionEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
    AVAILABLE;

  const staysSearchEnabled =
    getExperimentVariant(expState.experiments, STAYS_SEARCH) === AVAILABLE;

  const cacheHotelTokenEnabled =
    getExperimentVariant(expState.experiments, CACHE_HOTEL_TOKEN) === AVAILABLE;

  const { policies } = useContext(ClientContext);
  const showPolicyBanner = useShowPolicyBanner(policies);

  const renderEachLodging = (lodging?: Lodging) => {
    if (!lodging) return null;

    const isHome = lodging.stayType === StayType.Homes;

    const lodgingData = lodging.lodging;
    const { lodgingCollection } = lodging;

    const openShopPage = () => {
      let params;

      params = transformToStringifiedQuery({
        lodgingId: lodgingData.id,
        lodgingSelection: mapBounds
          ? {
              LodgingSelection: LodgingSelectionEnum.Location,
              descriptor: mapBounds,
            }
          : (searchLocation?.id as IIdLodgings).lodgingSelection,
        ...hotelQueryParams,
        roomsCount,
        petsCount,
        fromHotelAvailability:
          lodgingCollection === LodgingCollectionEnum.Premier ||
          lodgingCollection === LodgingCollectionEnum.Lifestyle
            ? true
            : undefined,
        hotelDetailsEntrySource: HotelDetailsEntrySourceEnum.MAP,
        shopToken: cacheHotelTokenEnabled
          ? lodging.price?.opaqueShopRequest
          : localCache.set(uuidv4(), lodging.price?.opaqueShopRequest || "")
              .key,
        encodedPersistentLodgingTrackingProperties:
          buildEncodedPersistentLodgingTrackingProperties(lodging),
      });

      if (isHome && staysSearchEnabled) {
        // Vacation Rentals (Homes) uses listingId instead of lodgingId in the shop URL
        params = params.replace("lodgingId", "listingId");
      }

      trackEvent({
        eventName: SELECTED_HOTEL_FROM_MAP,
        properties: {
          ...viewedHotelListProperties.properties,
          ...getLodgingTrackingProperties(lodging).properties,
          is_preferred_cot: lodging.isPreferred,
        },
        encryptedProperties: [
          ...viewedHotelListProperties.encryptedProperties,
          ...getLodgingTrackingProperties(lodging).encryptedProperties,
        ],
      });

      const path =
        isHome && staysSearchEnabled
          ? PATH_VACATION_RENTAL_SHOP
          : lodgingCollection === LodgingCollectionEnum.Premier ||
            lodgingCollection === LodgingCollectionEnum.Lifestyle
          ? PREMIER_COLLECTION_PATH_SHOP
          : PATH_SHOP;

      history.push(`${path}${params}`, { fromPage: location.pathname });
    };

    const showPCStyling =
      isPremierCollectionEnabled &&
      lodgingCollection === LodgingCollectionEnum.Premier;
    const showLCStyling =
      (isPremierCollectionEnabled || isLifestyleCollectionEnabled) &&
      lodgingCollection === LodgingCollectionEnum.Lifestyle;

    return (
      <Box
        className={clsx(
          "mobile-lodging-availability-map-rolling-gallery-wrapper",
          {
            "premier-collection": showPCStyling,
            "lifestyle-collection": showLCStyling,
          }
        )}
      >
        {showPCStyling && (
          <>
            <PremierCollectionBenefitsModal
              openBenefitsModal={openBenefitsModal}
              handleClosePCBenefitsModal={() => {
                setOpenBenefitsModal(false);
              }}
              isMobile
              variant="premier-collection"
              largestValueAccount={largestValueAccount}
            />
            <ButtonWrap
              onClick={() => setOpenBenefitsModal(true)}
              className="benefits-modal-button"
              aria-label="benefits modal button"
            >
              <Typography className="modal-button-text">
                {textConstants.PC_BENEFITS_BUTTON_TEXT}
              </Typography>
              <Icon name={IconName.InfoCircle} />
            </ButtonWrap>
            <Icon name={IconName.PremierCollectionRibbon} />
          </>
        )}
        {showLCStyling && (
          <>
            <PremierCollectionBenefitsModal
              openBenefitsModal={openBenefitsModal}
              handleClosePCBenefitsModal={() => {
                setOpenBenefitsModal(false);
              }}
              isMobile
              variant={
                isPremierCollectionEnabled
                  ? "lifestyle-collection-premium"
                  : "lifestyle-collection-non-premium"
              }
              isUserPrimary={isUserPrimary}
              tenant={config.TENANT}
              largestValueAccount={largestValueAccount}
            />
            <ButtonWrap
              onClick={() => setOpenBenefitsModal(true)}
              className="benefits-modal-button"
              aria-label="benefits modal button"
            >
              <Typography className="modal-button-text">
                {textConstants.LC_BENEFITS_BUTTON_TEXT}
              </Typography>
              <Icon name={IconName.InfoCircle} />
            </ButtonWrap>
            <Icon name={IconName.LifestyleCollectionRibbon} />
          </>
        )}
        <span
          className={clsx("mobile-hotel-tooltip-close-button", config.TENANT)}
          onClick={(e) => {
            e.stopPropagation();
            setLodgingIdInFocus(null);
          }}
        >
          <Icon name={IconName.CloseXCircle} />
        </span>
        <MobileHotelMapTooltip
          lodging={lodging}
          isHome={isHome}
          onClick={openShopPage}
          nightCount={nightCount || 0}
          rewardsKey={selectedAccountId ?? ""}
          isLodgingPromotionsExperiment={isLodgingPromotionsExperiment}
          isVentureX={isVentureX}
          roomsCount={roomsCount}
          showPolicyBanner={showPolicyBanner}
          isPayAtHotelBadgeEnabled={isPayAtHotelBadgeEnabled && isAllowedPayLaterLodging(allowedPayLaterLodgingIds, lodgingData.id)}
        />
      </Box>
    );
  };

  return renderEachLodging(lodgingInFocus);
};
